import { Table, TableBody, TableCell, TableHeader, TableRow, tokens } from '@fluentui/react-components';
import { px } from '@this/app-shared/dist/tools/styles.js';
import { useMemo } from 'react';
import { FieldTableEmpty } from './empty/index.js';
import { FieldTableHeader } from './header/index.js';
import { FieldTableLoading } from './loading/index.js';
import { FieldTableRow } from './row/index.js';
import { useSharedStyles } from './shared-styles.js';
import { useStyles } from './styles.js';
import { FieldTableTotal } from './total/index.js';
import type { FieldTableProps, FieldValueRecord } from './types.js';

export function FieldTable<TValue extends Array<FieldValueRecord>>({
    fieldValue,
    emptyMessage,
    maxRows,
    columnSizes,
    tableCells,
    totalField,
}: FieldTableProps<TValue>) {
    const styles = useStyles();
    const sharedStyles = useSharedStyles();

    const columnSizesSortedByTableCells = useMemo(() => {
        const sortedColumnSizes = tableCells.map((cell) => {
            const columnSize = columnSizes.find((size) => size.key === cell.key);

            return columnSize?.size || '1fr';
        });

        return sortedColumnSizes.join(' ');
    }, [columnSizes, tableCells]);

    const cellsToRight = useMemo(() => {
        // create a record with the cells that should be aligned to the right
        return columnSizes.reduce(
            (acc, curr) => {
                acc[curr.key as keyof TValue[number]] = !!curr.alignRight;

                return acc;
            },
            {} as Record<keyof TValue[number], boolean>
        );
    }, [columnSizes]);

    const totalRows = fieldValue?.length ?? 0;
    const maxRowsMinusOffset = maxRows ? maxRows - 0.5 : undefined;

    return (
        <Table
            noNativeElements
            className={styles.fieldTable}
            style={{
                gridTemplateColumns: columnSizesSortedByTableCells,
                maxHeight: maxRowsMinusOffset
                    ? `calc((${tokens.spacingHorizontalXXL} * ${2}) + ${px(36 * maxRowsMinusOffset)})`
                    : undefined,
            }}
        >
            <TableHeader className={sharedStyles.tableDisplayContents}>
                <FieldTableHeader tableCells={tableCells} cellsToRight={cellsToRight} />
            </TableHeader>

            {
                <TableBody className={sharedStyles.tableDisplayContents}>
                    {typeof fieldValue === 'undefined' ? (
                        <FieldTableLoading tableCells={tableCells} />
                    ) : totalRows === 0 ? (
                        <FieldTableEmpty message={emptyMessage} />
                    ) : (
                        fieldValue.map((value, valueIndex) => (
                            <FieldTableRow
                                key={valueIndex}
                                value={value}
                                tableCells={tableCells}
                                cellsToRight={cellsToRight}
                            />
                        ))
                    )}

                    {!!totalRows && !!totalField && (
                        <FieldTableTotal fieldValue={fieldValue} totalField={totalField} totalRows={totalRows} />
                    )}

                    {/* HACK: Preserve the padding at the bottom, if not set the bottom will collapse */}
                    {!!totalRows && totalRows > (maxRowsMinusOffset ?? 0) && (
                        <TableRow className={sharedStyles.tableDisplayContents}>
                            <TableCell className={sharedStyles.tableDisplayContents}>
                                <div className={styles.preserveTableBottomPadding} />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            }
        </Table>
    );
}
